import { supertypeClass, property, Persistable, Remoteable, Supertype } from '@haventech/amorphic';
import { Customer } from './Customer';
import { DiagnosisShort } from './DiagnosisShort';
import { DiagnosisExtra } from './DiagnosisExtra';

/***
 * The CIR Supplement represents a standalone table of questions which are asked only
 * when the client has been quoted with CIR
 *
 * Need to migrate this into ACORD, this new duplicate diagnosis table is created due to `overflow issue- join query
 * exceeding max no. of columns in Diagnosis`. pls refer https://haventech.atlassian.net/browse/HL-51435
 */
@supertypeClass({ toClient: true, toServer: true })
export class ChronicIllnessRiderSupplement extends Remoteable(Persistable(Supertype)) {
  @property({
    getType: () => Customer,
    comment: 'Customer to which this policy is attached.',
  })
  customer: Customer;

  /** Lifestyle Questions **/
  @property({
    comment:
      'currently need human assistance or supervision with bathing, maintaining continence, dressing, eating, transferring from bed to chair or toileting?',
  })
  requiresHygieneAid: boolean;

  @property({ comment: 'More details for requiresHygieneAid' })
  hygieneAidDetails: string;

  @property({
    comment: 'currently use a wheelchair, walker, multi-prongcane, hospital bed, oxygen, motorized cart or stair lift?',
  })
  requiresMachineAid: boolean;

  @property({ comment: 'More details for requiresMachineAid' })
  machineAidDetails: string;

  @property({
    comment:
      'need help, assistance or supervision in performing - taking medication, doing housework, laundry, shopping or meal preparation',
  })
  requiresChoreAid: boolean;

  @property({ comment: 'More details for requiresChoreAid' })
  choreAidDetails: string;

  /** Lifetime Medical Questions (no diagnosis -- short response only) **/
  @property({
    comment: 'Alzheimer Disease',
  })
  hasAlzheimers: boolean;

  @property({ comment: 'More details for hasAlzheimers' })
  hasAlzheimersDetails: string;

  @property({
    comment: 'Amyotrophic Lateral Sclerosis (Lou Gehrigs Disease)',
  })
  hasAls: boolean;

  @property({ comment: 'More details for hasAls' })
  hasAlsDetails: string;

  @property({
    comment: 'Cerebral Palsy',
  })
  hasCerebralPalsy: boolean;

  @property({ comment: 'More details for hasCerebralPalsy' })
  hasCerebralPalsyDetails: string;

  @property({
    comment: 'Cystic Fibrosis',
  })
  hasCysticFibrosis: boolean;

  @property({ comment: 'More details for hasCysticFibrosis' })
  hasCysticFibrosisDetails: string;

  @property({
    comment: 'Dementia',
  })
  hasDementia: boolean;

  @property({ comment: 'More details for hasDementia' })
  hasDementiaDetails: string;

  @property({
    comment: 'Huntingtons Chorea',
  })
  hasHuntingtons: boolean;

  @property({ comment: 'More details for hasHuntingtons' })
  hasHuntingtonsDetails: string;

  @property({
    comment: 'Liver Cirrhosis',
  })
  hasLiverCirrhosis: boolean;

  @property({ comment: 'More details for hasLiverCirrhosis' })
  hasLiverCirrhosisDetails: string;

  @property({
    comment: 'Muscular Dystrophy',
  })
  hasMuscularDystrophy: boolean;

  @property({ comment: 'More details for hasMuscularDystrophy' })
  hasMuscularDystrophyDetails: string;

  @property({
    comment: 'Myasthenia Gravis',
  })
  hasMyastheniaGravis: boolean;

  @property({ comment: 'More details for hasMyastheniaGravis' })
  hasMyastheniaGravisDetails: string;

  @property({
    comment: 'Organic Brain Syndrome',
  })
  hasBrainSyndrome: boolean;

  @property({ comment: 'More details for hasBrainSyndrome' })
  brainSyndromeDetails: string;

  @property({
    comment: 'Paralysis',
  })
  hasParalysis: boolean;

  @property({ comment: 'More details for hasParalysis' })
  hasParalysisDetails: string;

  @property({
    comment: 'Parkinsons Disease / Parkinsonism',
  })
  hasParkinsons: boolean;

  @property({ comment: 'More details for hasParkinsons' })
  hasParkinsonsDetails: string;

  @property({
    comment: 'Schizophrenia',
  })
  hasSchizophrenia: boolean;

  @property({ comment: 'More details for hasSchizophrenia' })
  hasSchizophreniaDetails: string;

  /** 10 Year Medical Questions (diagnosis info required) **/
  @property({
    comment: 'Amnesia',
  })
  hadAmnesia: boolean;

  @property({
    getType: () => DiagnosisShort,
    comment: 'amenesiaDiagnosis',
  })
  amenesiaDiagnosis: DiagnosisShort;

  @property({
    comment:
      'Any Hip; Knee; Shoulder or other Bone or Joint Condition (such as Arthritis); Osteoporosis; Amputation due to disease or other medical condition',
  })
  hadBoneOrJointCondition: boolean;

  @property({
    type: Array,
    getType: () => DiagnosisExtra,
    comment: 'boneOrJointConditionDiagnosis',
  })
  boneOrJointConditionDiagnosis: Array<DiagnosisExtra> = [];

  @property({
    comment: 'Ataxia; Transverse Myelitis; Motor Neuron Disease or Post-Polio Syndrome',
  })
  hadAtaxiaMyelitisNeuron: boolean;

  @property({
    type: Array,
    getType: () => DiagnosisExtra,
    comment: 'ataxiaMyelitisNeuronDiagnosis',
  })
  ataxiaMyelitisNeuronDiagnosis: Array<DiagnosisExtra> = [];

  @property({
    comment: 'A condition which causes limited motion',
  })
  hadLimitedMotion: boolean;

  @property({
    type: Array,
    getType: () => DiagnosisExtra,
    comment: 'limitedMotionDiagnosis',
  })
  limitedMotionDiagnosis: Array<DiagnosisExtra> = [];

  /** 5 Year Medical Questions (diagnosis info required) **/
  @property({
    comment: 'Memory loss',
  })
  hadMemoryLoss: boolean;

  @property({
    getType: () => DiagnosisShort,
    comment: 'memoryLossDiagnosis',
  })
  memoryLossDiagnosis: DiagnosisShort;

  @property({
    comment: 'Tremors or numbness',
  })
  hadTremorsOrNumbness: boolean;

  @property({
    getType: () => DiagnosisShort,
    comment: 'tremorNumbDiagnosis',
  })
  tremorNumbDiagnosis: DiagnosisShort;

  @property({
    comment: 'A fall; accident or imbalance which led to a neurological or physical injury',
  })
  hadFallLeadingToInjury: boolean;

  @property({
    type: Array,
    getType: () => DiagnosisExtra,
    comment: 'fallLeadingToInjuryDiagnosis',
  })
  fallLeadingToInjuryDiagnosis: Array<DiagnosisExtra> = [];

  /** Coverage Information **/
  @property({
    comment:
      'During the last 12 months, received any type of disability benefit, Worker’s Compensation or Social Security Disability',
  })
  receivedDisabilityBenefits: boolean;

  @property({
    comment: 'covered by Medicaid',
  })
  coveredByMedicaid: boolean;

  @property({
    comment: 'Within the last 5 years, had an application for Long Term Care insurance declined or rated',
  })
  cirRecentlyRejected: boolean;

  @property({
    rule: ['currencyZero', 'required', 'currencyRoundedConditionally'],
    comment:
      'provide the amount of long-term care insurance or chronic illness coverage currently have in force at MassMutual or any other company',
  })
  cirInForceCoverageAmount: number;

  constructor() {
    super();
  }

  static new(customer: Customer) {
    return new ChronicIllnessRiderSupplement().init(customer);
  }

  init(customer) {
    this.customer = customer;
    return this;
  }

  hadAtaxiaMyelitisNeuronTrigger() {
    if (this.hadAtaxiaMyelitisNeuron === true) {
      if (this.ataxiaMyelitisNeuronDiagnosis.length === 0) {
        this.ataxiaMyelitisNeuronDiagnosis.push(DiagnosisExtra.new(this.customer));
      }
    } else {
      this.ataxiaMyelitisNeuronDiagnosis = [];
    }
  }

  hadBoneOrJointConditionTrigger() {
    if (this.hadBoneOrJointCondition === true) {
      if (this.boneOrJointConditionDiagnosis.length === 0) {
        this.boneOrJointConditionDiagnosis.push(DiagnosisExtra.new(this.customer));
      }
    } else {
      this.boneOrJointConditionDiagnosis = [];
    }
  }

  hadLimitedMotionTrigger() {
    if (this.hadLimitedMotion === true) {
      if (this.limitedMotionDiagnosis.length === 0) {
        this.limitedMotionDiagnosis.push(DiagnosisExtra.new(this.customer));
      }
    } else {
      this.limitedMotionDiagnosis = [];
    }
  }

  hadFallLeadingToInjuryTrigger() {
    if (this.hadFallLeadingToInjury === true) {
      if (this.fallLeadingToInjuryDiagnosis.length === 0) {
        this.fallLeadingToInjuryDiagnosis.push(DiagnosisExtra.new(this.customer));
      }
    } else {
      this.fallLeadingToInjuryDiagnosis = [];
    }
  }
}
